import React, { useState, useEffect } from "react"
import { useAuth } from "./AuthContext"

const EMAIL_FOR_SIGN_IN_KEY = "emailForSignIn"

export function AcceptInvite() {
  const { completeSignInWithLink, pendingInviteCode } = useAuth()
  const [email, setEmail] = useState("")
  const [displayName, setDisplayName] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlEmail = params.get("email")
    const storedEmail = localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY)

    if (urlEmail) {
      setEmail(urlEmail)
    } else if (storedEmail) {
      setEmail(storedEmail)
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()

    if (!email.trim()) {
      setError("Please enter your email address")
      return
    }

    try {
      setError("")
      setLoading(true)
      const joinResult = await completeSignInWithLink(email.trim(), displayName.trim())
      setResult(joinResult)
    } catch (err) {
      if (err.code === "auth/invalid-action-code") {
        setError("This invite link has expired or already been used. Please request a new one.")
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter the email address where you received the invitation.")
      } else {
        setError(err.message || "Failed to complete sign-in. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50 py-12 px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">You're in!</h2>
          <p className="text-gray-600">
            You've joined <span className="font-semibold">{result.studioName}</span> as a{" "}
            <span className="font-semibold">{result.role}</span>.
          </p>
          <p className="text-sm text-gray-500">Loading the app now...</p>
        </div>
      </div>
    )
  }

  const isEmailPreFilled = !!email

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Studio Ad Maker</h1>
          <h2 className="text-xl font-semibold text-gray-900">You've been invited!</h2>
          <p className="mt-2 text-sm text-gray-600">
            {pendingInviteCode
              ? "Complete the form below to accept your studio invitation."
              : "Sign in to continue."}
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="invite-email"
                type="email"
                required
                className={`appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isEmailPreFilled ? "bg-gray-50 text-gray-600" : "text-gray-900"}`}
                placeholder="Enter the email where you received the invite"
                value={email}
                onChange={e => setEmail(e.target.value)}
                readOnly={isEmailPreFilled}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isEmailPreFilled
                  ? "This is the email your invitation was sent to."
                  : "Enter the email address where you received the invitation."}
              </p>
            </div>

            <div>
              <label htmlFor="invite-display-name" className="block text-sm font-medium text-gray-700 mb-1">
                Your Name
              </label>
              <input
                id="invite-display-name"
                type="text"
                required
                autoFocus
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter your name"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                This will be your display name in the studio.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? "Joining studio..." : "Accept Invitation & Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
